import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../../../firebase/firebase";
import EarningsMappingSection from "../EarningsMappingSection";
import { saveAndMergeMappings } from "../earningsSaver";
import {
  detectDailyPlatform,
  detectReportDateFromFileName,
  normalizeDailyRows,
  readDailyRideHailingFile,
} from "./dailyRideHailingParser";
import { saveDailyRideHailingEarnings } from "./dailyRideHailingSaver";
import DailyRideHailingUploadHistory from "./DailyRideHailingUploadHistory";

const COMPANIES = [
  {
    value: "Comfort Line Luxury Motor Vehicles Services LLC",
    label: "Comfort Line",
  },
  {
    value: "Lolia Limousine for Luxury Motor Vehicles Services LLC",
    label: "Lolia Limousine",
  },
];

const money = (v) => Number(v || 0).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DailyRideHailingUpload() {
  const [company, setCompany] = useState("");
  const [platform, setPlatform] = useState("uber");
  const [reportDate, setReportDate] = useState("");
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [driverList, setDriverList] = useState([]);
  const [existingMappings, setExistingMappings] = useState({});
  const [mappingUpdates, setMappingUpdates] = useState({});
  const [unmappedRows, setUnmappedRows] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [detectedFileDate, setDetectedFileDate] = useState("");

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "drivers"), (snap) => {
      setDriverList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!platform) return undefined;
    const unsub = onSnapshot(doc(db, "driverPlatformMappings", platform), (snap) => {
      setExistingMappings(snap.data() || {});
    });
    return () => unsub();
  }, [platform]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.bank += Number(row.bank_income || 0);
        acc.cash += Number(row.cash_income || 0);
        return acc;
      },
      { bank: 0, cash: 0 }
    );
  }, [rows]);

  const resetPreview = () => {
    setRows([]);
    setUnmappedRows([]);
    setMappingUpdates({});
    setStatus("");
    setDetectedFileDate("");
  };

  const handleFile = (selected) => {
    setFile(selected || null);
    resetPreview();
  };

  async function processFile() {
    if (!company) return alert("Select company first.");
    if (!file) return alert("Select Uber or Bolt daily report.");

    setProcessing(true);
    setStatus("");
    try {
      const rawRows = await readDailyRideHailingFile(file);
      const detected = detectDailyPlatform(rawRows);
      const usePlatform = detected || platform;

      if (detected && detected !== platform) {
        setPlatform(detected);
      }

      const normalized = normalizeDailyRows(rawRows, usePlatform);
      if (!normalized.length) throw new Error("No driver rows found in this report.");

      const detectedDate = detectReportDateFromFileName(file.name, usePlatform);
      setDetectedFileDate(detectedDate || "");
      // Prefer the date encoded in the portal filename. This avoids saving, for
      // example, a 6-Oct report under 7-Oct by mistake. The field remains editable
      // for exceptional files that do not carry a usable date.
      if (detectedDate) setReportDate(detectedDate);

      const mappingDoc = await getDoc(doc(db, "driverPlatformMappings", usePlatform));
      const mappingsSnap = mappingDoc.exists() ? mappingDoc.data() || {} : {};
      setExistingMappings(mappingsSnap);

      const missingMap = new Map();
      normalized.forEach((r) => {
        if (!mappingsSnap[r.driver_id] && !missingMap.has(r.driver_id)) {
          missingMap.set(r.driver_id, { ...r, platform: usePlatform });
        }
      });

      setRows(normalized);
      setUnmappedRows(Array.from(missingMap.values()));
      setStatus(
        missingMap.size
          ? `${normalized.length} rows read. Map ${missingMap.size} driver(s), then save.`
          : `${normalized.length} rows ready to save.`
      );
    } catch (err) {
      console.error(err);
      alert(`Daily report could not be processed: ${err?.message || err}`);
    } finally {
      setProcessing(false);
    }
  }

  async function saveMappingsOnly() {
    const missingSelections = unmappedRows.filter((r) => !mappingUpdates[r.driver_id]);
    if (missingSelections.length) {
      alert("Please map every listed driver before continuing.");
      return;
    }

    try {
      const merged = await saveAndMergeMappings(mappingUpdates, existingMappings, platform);
      setExistingMappings(merged);
      setUnmappedRows([]);
      setMappingUpdates({});
      setStatus(`${rows.length} rows ready to save.`);
      alert("Driver mappings saved. You can now save the daily report.");
    } catch (err) {
      console.error(err);
      alert(`Could not save driver mappings: ${err?.message || err}`);
    }
  }

  async function saveReport() {
    if (!company) return alert("Select company.");
    if (!platform) return alert("Select platform.");
    if (!reportDate) return alert("Confirm the report date.");
    if (!file || !rows.length) return alert("Process a daily report first.");
    if (unmappedRows.length) return alert("Map all drivers before saving this daily report.");
    if (detectedFileDate && reportDate !== detectedFileDate) {
      const ok = window.confirm(
        `The portal filename indicates ${detectedFileDate}, but you selected ${reportDate}.\n\n` +
          `Save under ${reportDate} anyway?`
      );
      if (!ok) return;
    }

    setSaving(true);
    try {
      const result = await saveDailyRideHailingEarnings({
        rows,
        platform,
        company,
        reportDate,
        mappings: existingMappings,
        sourceFileName: file.name,
        uploadedBy: auth.currentUser?.email || "",
      });

      setStatus(
        `Saved ${result.savedCount} driver rows. Bank AED ${money(result.bankTotal)} / Cash AED ${money(result.cashTotal)}.`
      );
      alert(
        `Daily ${platform.toUpperCase()} report saved.\n` +
          `Date: ${reportDate}\n` +
          `Drivers: ${result.savedCount}\n` +
          `Bank: AED ${money(result.bankTotal)}\n` +
          `Cash: AED ${money(result.cashTotal)}\n` +
          (result.replacedCount ? `Replaced previous rows: ${result.replacedCount}` : "")
      );
    } catch (err) {
      console.error(err);
      alert(`Daily report save failed: ${err?.message || err}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ padding: 22, maxWidth: 1050 }}>
      <h2 style={{ marginTop: 0 }}>Daily Ride-Hailing Upload</h2>
      <div style={{ padding: 14, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, marginBottom: 18 }}>
        <strong>Management reporting only.</strong> These daily Uber/Bolt uploads are stored separately and are not used by Car Sheets.
        Select the <strong>Income Company</strong> shown on the Uber/Bolt portal report. Revenue stays with that selected company even if the driver is using the other company&apos;s car.
        Existing weekly Uber/Bolt/Yango uploads and existing POS uploads remain unchanged. Uber company settlement / bank-transfer rows for Comfort Line and Lolia are ignored automatically. Any genuine new/unmapped driver is still shown below for mapping.
      </div>

      <div style={grid}>
        <label style={fieldLabel}>
          Income Company
          <select value={company} onChange={(e) => setCompany(e.target.value)} style={input}>
            <option value="">Select company</option>
            {COMPANIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </label>

        <label style={fieldLabel}>
          Platform
          <select value={platform} onChange={(e) => { setPlatform(e.target.value); resetPreview(); }} style={input}>
            <option value="uber">Uber</option>
            <option value="bolt">Bolt</option>
          </select>
        </label>

        <label style={fieldLabel}>
          Report date
          <input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} style={input} />
          {detectedFileDate && (
            <span style={{ fontSize: 12, color: reportDate === detectedFileDate ? "#15803d" : "#b91c1c", fontWeight: 600 }}>
              File date: {detectedFileDate}{reportDate !== detectedFileDate ? " — selected date is different" : ""}
            </span>
          )}
        </label>

        <label style={fieldLabel}>
          Daily report file
          <input type="file" accept=".csv,.xlsx" onChange={(e) => handleFile(e.target.files?.[0])} style={input} />
        </label>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
        <button onClick={processFile} disabled={processing} style={button("#2563eb")}>
          {processing ? "Processing..." : "Process Daily Report"}
        </button>
        <button onClick={saveReport} disabled={saving || !rows.length || !!unmappedRows.length} style={button("#15803d")}>
          {saving ? "Saving..." : "Save Daily Report"}
        </button>
      </div>

      {rows.length > 0 && (
        <div style={{ marginTop: 18, padding: 14, border: "1px solid #ddd", borderRadius: 8, background: "#fff" }}>
          <strong>Preview:</strong> {platform.toUpperCase()} — {reportDate || "confirm date"} — {rows.length} driver rows
          <div style={{ marginTop: 8 }}>
            Bank total: <strong>AED {money(totals.bank)}</strong> &nbsp; | &nbsp;
            Cash total: <strong>AED {money(totals.cash)}</strong>
          </div>
          {status && <div style={{ marginTop: 8, color: "#475569" }}>{status}</div>}
        </div>
      )}

      <EarningsMappingSection
        unmappedRows={unmappedRows}
        driverList={driverList}
        mappingUpdates={mappingUpdates}
        setMappingUpdates={setMappingUpdates}
        onSaveMapping={saveMappingsOnly}
      />

      <DailyRideHailingUploadHistory company={company} platform={platform} />
    </div>
  );
}

const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 };
const fieldLabel = { display: "flex", flexDirection: "column", gap: 6, fontWeight: 700 };
const input = { padding: 9, border: "1px solid #cbd5e1", borderRadius: 6, background: "white" };
const button = (background) => ({ padding: "10px 16px", border: "none", borderRadius: 6, color: "white", background, fontWeight: 700, cursor: "pointer" });
